




















































































































































































































import { Component, Prop, Vue } from 'vue-property-decorator';

import Page from '@/componentsV2/molecules/Page/Page.vue';
import Typography from '@/componentsV2/atoms/Typography/Typography.vue';
import Checkbox from '@/components/Checkbox/Checkbox.vue';
import InfoIcon from '@/components/icons/InfoV2.vue';
import { EBillingStatusTypes } from '@/store/modules/businessTypes';
import PerformanceItem from '@/components/molecules/PerformanceItem/PerformanceItem.vue';
import { ICardPerformance } from '@/models/performance';
import BillingPlanOverview from '@/componentsV2/molecules/BillingPlanOverview/BillingPlanOverview.vue';
import PinIcon from '@/components/icons/Pin.vue';
import FilmIcon from '@/components/icons/Film.vue';
import CoinNetworkPlanList from './CoinNetworkPlanList.vue';
import eventBus from '@/utils/eventBus';
import { ModalOptionsV2 } from '@/components/Modal/ModalOptions';
import CoinNetworkAcceptanceModal from '@/componentsV2/templates/Modals/CoinNetworkAcceptanceModal/CoinNetworkAcceptanceModal.vue';
import BillingService from '@/api/BillingService';
import DateUtils from '@/utils/DateUtils';
import { abbreviateNumber } from '@/utils/currency';
import ClockSolidIcon from '@/components/icons/ClockSolid.vue';
import flagKeys from '@/constants/flagKeys';
import segmentIo from '@/utils/segment.io';
import { getNeedsOnboardingCall } from '@/utils/onboardingCall';
import {
  getBillingIsFreeCohort,
  getBillingIsHidden,
  getBillingIsPending,
  getBillingOnboardingCallPending
} from '@/utils/billing';
import { IBusinessType } from '@/models/business';

@Component({
  components: {
    Page,
    Typography,
    Checkbox,
    InfoIcon,
    PerformanceItem,
    BillingPlanOverview,
    PinIcon,
    FilmIcon,
    CoinNetworkPlanList,
    CoinNetworkAcceptanceModal,
    ClockSolidIcon
  }
})
export default class CoinNetworkPlan extends Vue {
  @Prop({ default: false }) forceShowToggle?: boolean;
  service = new BillingService();

  loading = false;
  features: any = {};
  coinsWithTwism: any = ' ';
  coinsToCustomers: any = ' ';
  subscription: any = {};

  get businessType() {
    return this.$store.getters['business/getBusinessInfo'].business.type;
  }

  get isNewBillingEnabled() {
    return (
      this.flags.includes(flagKeys.billingV2) &&
      this.businessType === IBusinessType.INSTORE
    );
  }

  get performances(): ICardPerformance[] {
    return [
      {
        label: 'Coins to Twism',
        counter: this.coinsWithTwism,
        description: 'Total number of branded Coins held by Twism.'
      },
      {
        counter: this.coinsToCustomers,
        label: 'Coins to Members',
        description: 'Lifetime total number of Coins received by members.'
      }
    ];
  }

  get actions() {
    const watchVideo = {
      label: 'Watch the Coin Network video',
      icon: 'FilmIcon',
      onClick: () => {
        segmentIo.track('Clicked Coin Network Watch Video');
        window.open('https://youtu.be/E7VaLrxOOKI', '_blank');
      }
    };

    const manageLocations = {
      label: 'Manage my locations',
      icon: 'PinIcon',
      onClick: () => {
        segmentIo.track('Clicked Coin Network Manage Locations');
        this.$router.push('/settings/locations');
      }
    };

    // The flag takes the Coin Network out of the merchant's vocabulary, and the
    // video is the loudest mention left on this screen (TWISM-19351).
    return this.billingIsHidden
      ? [manageLocations]
      : [watchVideo, manageLocations];
  }

  get businessInfo() {
    return this.$store.getters['business/getBusinessState'] || {};
  }

  get subscribedBilling() {
    return (
      this.billingStatus === EBillingStatusTypes.SUBSCRIBED ||
      this.billingStatus === EBillingStatusTypes.DELAY_PERIOD ||
      getBillingOnboardingCallPending()
    );
  }

  get billingStatus() {
    return this.businessInfo.subscriptionStatus;
  }

  get startMitingDate() {
    return DateUtils.formattedDate({
      date: this.subscription.nextPaymentAt
    });
  }

  // The empty state this drives mounts the acceptance modal, whose success path
  // subscribes and activates — a subscribe flow the free cohort must never be
  // offered, right under a banner telling them there is nothing to do
  // (TWISM-19180). Gating the computed rather than the branch also keeps the
  // page's empty-state class, config and description consistent with the body
  // that actually renders.
  get pendingBilling() {
    return getBillingIsPending() && !this.freeCohort;
  }

  // The migrated cohort keeps this screen while billing is hidden, but it has no
  // plan left to activate or deactivate — the free banner replaces the status
  // toggle (TWISM-19180).
  get freeCohort() {
    return getBillingIsFreeCohort();
  }

  get billingIsHidden() {
    return getBillingIsHidden();
  }

  // The cohort is no longer charged, so the weekly-collection sentence would
  // contradict the banner right above it — on the page header and on the
  // `forceShowToggle` block alike, which is why both read this one string
  // (TWISM-19180). What is left still describes the screen they are on.
  get planDescription() {
    const intro =
      'Your business is now part of the Coin Network! Here you can track your Coin minting and circulation.';

    return this.freeCohort
      ? intro
      : `${intro} Twism collects 25 of your branded Coins per week for each of your locations.`;
  }

  get toggleLabel() {
    return {
      activeLabel: 'Active',
      inactiveLabel: 'Inactive'
    };
  }

  get tooltipMessage() {
    if (this.needsOnboardingCall) return 'Waiting for profile activation.';

    const date = DateUtils.formattedDate({
      date: this.subscribedBilling
        ? this.subscription.createdAt || this.subscription.startAt
        : this.subscription.endAt
    });

    return `Coin Network plan ${
      this.subscribedBilling ? 'activated' : 'deactivated'
    } on ${date}.`;
  }

  get needsOnboardingCall() {
    return getNeedsOnboardingCall();
  }

  get bannerTitle() {
    if (this.needsOnboardingCall)
      return `Your coin minting will start ${
        this.subscription.defaultDelayPeriod
      } days after your profile is activated.`;

    return `Your Coin minting will start on ${this.startMitingDate}`;
  }

  get flags() {
    return this.$store.getters['flags/getFlags'] || [];
  }

  get showDelayedPeriodDisclaimer() {
    return (
      (this.billingStatus === EBillingStatusTypes.DELAY_PERIOD &&
        this.subscription.eligibleForDelayPeriod &&
        this.flags.includes(flagKeys.billingModuleV3)) ||
      (!this.loading && getBillingOnboardingCallPending())
    );
  }

  async loadOverviewData() {
    try {
      this.$showLoaderWithLocker();
      this.loading = true;
      const [{ data }, { data: features }] = await Promise.all([
        this.service.getBillingOverview(),
        this.service.getActivedFeatures()
      ]);

      this.$store.dispatch(
        'business/SET_BILLING_SUBSCRIPTION',
        data.subscription
      );

      this.features = features;
      this.coinsWithTwism = abbreviateNumber(
        Number(data.coinsWithTwism || 0),
        0
      );
      this.coinsToCustomers = abbreviateNumber(
        Number(data.coinsToCustomers || 0),
        0
      );

      this.subscription = data.subscription;
    } finally {
      this.loading = false;
      this.$hideLoader();
    }
  }

  onSubscribed() {
    this.loadOverviewData();
  }

  async changedActivation() {
    eventBus.$emit(
      'openSubModal',
      new ModalOptionsV2({
        componentToRender: this.subscribedBilling
          ? `coinNetworkDeactivationModal`
          : `coinNetworkReactivationModal`,
        hideCancelButton: false,
        icon: '',
        mobileFullScreen: true
      }),
      {
        subscription: this.subscription,
        features: this.features,
        origin: 'Coin Network Page Toggle'
      }
    );
  }

  mounted() {
    if (!this.pendingBilling) {
      this.loadOverviewData();
    }
  }

  created() {
    eventBus.$on('reloadCoinNetworkOverview', this.loadOverviewData);
  }

  beforeDestroy() {
    eventBus.$off('reloadCoinNetworkOverview', this.loadOverviewData);
  }
}
